import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../../config/theme';
import Typography from '@material-ui/core/Typography';
import { numberAsCurrency, minPurchasableTickets } from '../../utils';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: '14px',
    backgroundColor: '#2D6E2F',
    padding: '18px',
    color: 'white',
    margin: '20px 0 12px',
  },
  title: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: '16px',
    color: '#FFFFFF',
    '& span': {
      color: '#56F84B',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '105%',
    },
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: '14px',
    gap: '8px',
  },
  bigButton: {
    backgroundColor: '#183419',
    touchAction: 'manipulation',
    color: 'white',
    fontSize: '1.45rem',
    fontWeight: 600,
    borderRadius: '8px',
    height: '72px',
    display: 'inline-block',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
    flexBasis: 'calc(50% - 4px)',
    padding: '10px 12px',
    transition: 'transform 0.12s ease, filter 0.12s ease',
    '& small': {
      color: 'white',
      display: 'block',
      fontWeight: 400,
      fontSize: '0.68rem',
      textTransform: 'uppercase'
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.97)',
      filter: 'brightness(1.08)',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.1rem',
    },
  },
  mainButton: {
    border: '2px solid #28A745',
    position: 'relative',
    '&:before': {
      content: '"Mais Popular"',
      position: 'absolute',
      fontSize: '0.6rem',
      top: '-8px',
      left: '8px',
      borderRadius: '8px',
      color: '#193419',
      backgroundColor: '#00FF00',
      fontWeight: 700,
      padding: '2px 7px'
    }
  },
  fineSelection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: '10px',
    padding: '12px',
    marginTop: '8px',
  },
  shotTiersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '10px',
    marginBottom: '2px',
  },
  offerBadge: {
    backgroundColor: '#56F84B',
    color: '#183419',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '0.8rem',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: '12px',
  },
  shotTierChip: {
    backgroundColor: '#183419',
    color: '#FFFFFF',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '0.8rem',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    touchAction: 'manipulation',
    transition: 'transform 0.12s ease, filter 0.12s ease',
    '&:active': {
      transform: 'scale(0.96)',
      filter: 'brightness(1.1)',
    },
  },
  button: {
    color: 'white',
    fontWeight: 600,
    borderRadius: '8px',
    touchAction: 'manipulation',
    cursor: 'pointer',
    fontSize: '1.35rem',
    margin: 0,
    backgroundColor: '#193419',
    width: '38px',
    height: '38px',
    lineHeight: '1',
    border: 'none',
    transition: 'transform 0.12s ease, filter 0.12s ease',
    '& span': {
      position: 'relative',
      top: '-1px'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.94)',
      filter: 'brightness(1.1)',
    },
  },
  selectedTickets: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    backgroundColor: '#E9F0FF',
    color: '#000000',
    padding: '0 14px',
    fontSize: '1.1rem',
    minHeight: '38px',
    flex: 1,
    margin: '0 10px',
  },
  buyButton: {
    backgroundColor: '#28A745',
    borderRadius: '8px',
    padding: '14px 18px',
    border: 'none',
    color: 'white',
    width: '100%',
    fontSize: '0.95rem',
    margin: '14px 0 16px 0',
    textAlign: 'center',
    cursor: 'pointer',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9rem',
      padding: '12px 14px'
    },
  },
  buyButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.65em',
    width: 'auto',
    flexWrap: 'nowrap',
  },
  buyButtonIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem',
    minWidth: '1.5rem',
    minHeight: '1.5rem',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    flexShrink: 0,
  },
  buyButtonArrow: {
    fontSize: '0.8125em',
    color: '#28A745',
    fill: '#28A745',
    display: 'block',
  },
  buyButtonTitle: {
    fontWeight: 700,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    minHeight: '1.5rem',
    fontSize: '1em',
  },
  buyButtonSubtitle: {
    fontSize: '0.78em',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.92)',
    lineHeight: 1,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '1.5rem',
    marginLeft: 'auto',
    backgroundColor: '#52B855',
    borderRadius: '10px',
    padding: '8px 12px',
  },
  // Linha extra do botão de compra: quantas caixinhas a seleção atual rende.
  buyButtonPrizes: {
    flexBasis: '100%',
    marginTop: '8px',
    fontSize: '0.8em',
    fontWeight: 700,
    textTransform: 'none',
    textAlign: 'center',
    color: '#FFFFFF',
  },
  bigButtonPrizes: {
    textTransform: 'none !important',
    fontWeight: '600 !important',
  },
}));

export default function SelectionBox({ selectedTickets, setSelectedTickets, purchaseCallback, campaign, loading, showTitle = true }) {
  const classes = useStyles();

  // Os botões "+N" sempre SOMAM sobre a seleção atual (que já abre no mínimo):
  // a operação configura "+10 / +25 / +40" contando "mínimo + N" pra bater nas
  // faixas das caixinhas. Clampa nos dois lados para não montar uma seleção
  // que o backend vai recusar.
  function numberButtonCallback(number) {
    const value = parseInt(number.replace('*', ''));
    const next = (Number(selectedTickets) || 0) + value;
    setSelectedTickets(Math.min(maxPurchasable, Math.max(minPurchasable, next)));
  }

  const gamifiedSub = (campaign?.active_subcampaigns || [])
    .filter((s) => !s.sold_out && s.gamification_enabled)
    .find(Boolean);

  const maxPurchasable = campaign?.max_amount == 0 ? 1000000 : (campaign?.max_amount || 1000000);
  const minPurchasable = minPurchasableTickets(campaign);
  const offerMin = campaign?.special_offer_min_amount;

  function stepTickets(delta) {
    const next = (Number(selectedTickets) || 0) + delta;
    setSelectedTickets(Math.min(maxPurchasable, Math.max(minPurchasable, next)));
  }

  // Títulos do pai necessários por bilhete de incentivo — é o que converte a
  // faixa do minigame (contada em bilhetes da sub) no botão "Compre N+".
  // Durante um boost a conta usa o rendimento boostado, igual à mintagem do
  // backend para pedidos criados na janela: proporção reduzida divide menos,
  // multiplicador (turbo) rende vários bilhetes por título.
  const activeBoost = gamifiedSub && gamifiedSub.active_boost;
  const ratio = (activeBoost && activeBoost.multiplier && (1 / activeBoost.multiplier))
    || (activeBoost && activeBoost.boosted_ratio)
    || (gamifiedSub && gamifiedSub.ratio)
    || 1;

  // Arredonda para cima: com multiplicador a conta cai em fração, e comprar
  // menos que o inteiro seguinte não alcança a faixa.
  const ticketsForTier = (tier) => Math.ceil(tier.min_tickets * ratio);

  const allTiers = gamifiedSub && Array.isArray(gamifiedSub.game_shot_tiers) && gamifiedSub.game_shot_tiers.length > 0
    ? [...gamifiedSub.game_shot_tiers].sort((a, b) => a.min_tickets - b.min_tickets)
    : [];
  const rawTiers = allTiers.filter((tier) => ticketsForTier(tier) >= minPurchasable && ticketsForTier(tier) <= maxPurchasable);
  const shotTiers = rawTiers.length > 0 ? rawTiers : null;

  // Quantas caixinhas uma compra de N títulos rende: maior faixa alcançada
  // (mesma regra de GamePrizeTiers#prizes_for no backend, em títulos do pai).
  function prizesForTickets(tickets) {
    const reached = allTiers.filter((tier) => ticketsForTier(tier) <= tickets);
    return reached.length > 0 ? Number(reached[reached.length - 1].shots) || 0 : 0;
  }
  const prizesLabel = (count) => `${count} ${count === 1 ? 'caixinha da sorte' : 'caixinhas da sorte'}`;
  const selectedPrizes = prizesForTickets(Number(selectedTickets) || 0);

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        {showTitle && (
          <Typography variant="h5" className={classes.title}>
            Quanto mais títulos comprar, maiores são <span>as chances de ganhar!</span>
          </Typography>
        )}
        {offerMin > 0 && (
          <div className={classes.offerBadge}>
            Oferta especial: compre a partir de {offerMin} {offerMin === 1 ? 'título' : 'títulos'} 🎉
          </div>
        )}
        <div className={classes.buttonsContainer}>
          {
            campaign?.buttons_array?.map((amount, index) => (
              <button disabled={loading} key={index} className={`${classes.bigButton} ${amount.includes("*") && classes.mainButton}`} onClick={() => numberButtonCallback(amount)}>
                +{amount.replace('*', '')}
                {(() => {
                  // Caixinhas que a seleção passa a render se somar este botão.
                  const after = prizesForTickets((Number(selectedTickets) || 0) + parseInt(amount.replace('*', '')));
                  return after > 0
                    ? <small className={classes.bigButtonPrizes}>🎁 {prizesLabel(after)}</small>
                    : <small>Selecionar</small>;
                })()}
              </button>
            ))
          }
        </div>
        {shotTiers && (
          <div className={classes.shotTiersContainer}>
            {shotTiers.map((tier) => (
              <button
                key={tier.min_tickets}
                className={classes.shotTierChip}
                disabled={loading}
                onClick={() => setSelectedTickets(ticketsForTier(tier))}
              >
                Compre {ticketsForTier(tier)}+ e abra {tier.shots} {tier.shots === 1 ? 'caixinha da sorte' : 'caixinhas da sorte'} 🎁
              </button>
            ))}
          </div>
        )}
        <div className={classes.fineSelection}>
          <button disabled={loading || selectedTickets <= minPurchasable} className={classes.button} onClick={() => stepTickets(-1)}><span>-</span></button>
          <span className={classes.selectedTickets}>{selectedTickets}</span>
          <button disabled={loading || selectedTickets >= maxPurchasable} className={classes.button} onClick={() => stepTickets(1)}><span>+</span></button>
        </div>
      </div>
      <button disabled={loading} className={classes.buyButton} onClick={() => purchaseCallback()}>
        <span className={classes.buyButtonLabel}>
          <span className={classes.buyButtonIconWrap}>
            <ArrowForwardIcon className={classes.buyButtonArrow} />
          </span>
          <span className={classes.buyButtonTitle}>Comprar títulos</span>
        </span>
        <span className={classes.buyButtonSubtitle}>{numberAsCurrency(selectedTickets * campaign?.ticket_price)}</span>
        {selectedPrizes > 0 && (
          <span className={classes.buyButtonPrizes} data-testid="buy-button-prizes">
            🎁 {prizesLabel(selectedPrizes)}
          </span>
        )}
      </button>
    </ThemeProvider>
  )
}